import { useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useModuleEnabled } from "../access/useFeatureAccess";
import { useAuthContext } from "../auth/AuthContext";
import { getPpToken, resolveIdentityEmail } from "../auth/ppToken";
import { featureFlags } from "../config/featureFlags";
import { resolveWorklistIdentity } from "../worklist/resolveWorklistIdentity";
import { useWorklistAccessGate } from "../worklist/useWorklistAccessGate";
import { buildWorklistMemberNavState } from "../worklist/worklistNavState";
import {
  PatientPriorityIndexProvider,
  HospitalEdEventsCard,
  HospitalEdEventsCardSkeleton,
  usePriorityPatients,
  HOSPITAL_ED_LOOKBACK_DAYS,
  type PriorityPatientMember,
} from "@astrana/ui/modules";

/**
 * Home-page summary widget for recent hospital / ED activity — reads the
 * SAME data source as providerPortalFrontend's Hospital/ED tab (the
 * PatientPriorityIndex panel's ADT lookback window), just rendered as a
 * Home-page card instead of a worklist tab.
 *
 * WHY A CONTAINER AND NOT A DIRECT MOUNT. `HospitalEdEventsCard`
 * (@astrana/ui) is deliberately presentational: it takes
 * `members: PriorityPatientMember[]` and derives its own rows/counts, and it
 * fetches nothing. The panel data therefore has to come from the same place
 * `/worklist` gets it — `usePriorityPatients`, which reads the module-scoped
 * RTK Query store — so this file supplies `<PatientPriorityIndexProvider>`
 * (the store + identity context) around a child that calls the hook. See
 * `UrgentPatientsWidget.tsx`, which this file mirrors, for the full
 * reasoning (identical gating/provider/loading posture).
 *
 * ACCESS. Identical preconditions to WorklistPage/UrgentPatientsWidget: the
 * VITE feature flag, the `PriorityWorklist` persona module, and
 * `useWorklistAccessGate`'s PP-bearer + IPA + NPI resolution. This widget
 * renders no gate, no spinner and no error — anything short of `"ready"`
 * renders nothing. The one exception Home makes lives in
 * `HomePpConnectPrompt`, which prompts for a Provider Portal sign-in on
 * `"no-pp-session"` so an unlinked provider isn't left with a blank page.
 *
 * Once the gate IS ready, the widget shows `HospitalEdEventsCardSkeleton`
 * while the panel pages in rather than leaving an empty slot — see the
 * `isComplete` gate below for why a partial panel must never be rendered as
 * the real card.
 */

export default function HospitalEdEventsWidget() {
  const worklistEnabled = useModuleEnabled("PriorityWorklist");
  if (!featureFlags.patientPriorityIndex || !worklistEnabled) return null;
  return <HospitalEdEventsWidgetGated />;
}

function HospitalEdEventsWidgetGated() {
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const accessGate = useWorklistAccessGate();

  const identityEmail = resolveIdentityEmail(accountLabel);

  // Stable ref so `getAccessToken` always reads the LATEST auth state without
  // changing its own identity (and therefore the module-scoped baseQuery
  // closure) on every token refresh. Same pattern as WorklistPage.tsx.
  const authRef = useRef({ selectedProvider, accessToken, identityEmail });
  authRef.current = { selectedProvider, accessToken, identityEmail };

  const getAccessToken = useCallback((): string => {
    const { selectedProvider: sp, accessToken: at, identityEmail: email } =
      authRef.current;
    return getPpToken(sp, at, email) ?? "";
  }, []);

  const companyId = accessGate.resolvedIpaId ?? "";

  // userId/userRole MUST be decoded off the PP bearer `getAccessToken` returns,
  // not the primary Entra/Pathways session token — see WorklistPage.tsx's own
  // note on the diverging `sub` claims.
  const ppBearerToken = getPpToken(selectedProvider, accessToken, identityEmail);
  const { userId: nativeUserId, userRole: nativeUserRole } =
    resolveWorklistIdentity(ppBearerToken);

  if (accessGate.status !== "ready") return null;

  return (
    <PatientPriorityIndexProvider
      ppApiBaseUrl={`${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`}
      companyId={companyId}
      getAccessToken={getAccessToken}
      // `status === "ready"` guarantees a Luhn-validated 10-digit NPI.
      pcpNpi={accessGate.npi ?? ""}
      ipaId={companyId}
      userId={nativeUserId ?? ""}
      userRole={nativeUserRole}
      userEmail={identityEmail}
    >
      <HospitalEdEventsPanel npi={accessGate.npi ?? ""} ipaId={companyId} />
    </PatientPriorityIndexProvider>
  );
}

function HospitalEdEventsPanel({ npi, ipaId }: { npi: string; ipaId: string }) {
  const navigate = useNavigate();

  const pcpNpi = useMemo(() => (npi ? [npi] : []), [npi]);
  const ipaIds = useMemo(() => (ipaId ? [ipaId] : []), [ipaId]);

  // Same year / lookback / gap arguments UrgentPatientsWidget passes (see its
  // own note quoting RiskEvidencePanelWidget: "copied so the two surfaces
  // resolve to the same cohort"), except `adtLookbackDays`, which this widget
  // pins to `HOSPITAL_ED_LOOKBACK_DAYS` instead of the urgent widget's literal
  // `30` (same value today, named so the Hospital/ED window can be reasoned
  // about on its own).
  const currentYear = new Date().getFullYear();
  const { items: members, isComplete, isError } = usePriorityPatients({
    pcpNpi,
    ipaId: ipaIds,
    serviceYear: currentYear,
    measureYear: currentYear,
    adtLookbackDays: HOSPITAL_ED_LOOKBACK_DAYS,
    includeGaps: true,
    skip: pcpNpi.length === 0 || ipaIds.length === 0,
  });

  // Row click → the member's profile, via the SAME astrana: fallback route
  // and nav-state shape WorklistPage/UrgentPatientsWidget use.
  const handleSelectPatient = useCallback(
    (member: PriorityPatientMember) => {
      const astranaId = String(member.astranaId);
      navigate(`/members/astrana:${encodeURIComponent(astranaId)}`, {
        state: buildWorklistMemberNavState(astranaId, {
          companyId: member.ipaId || ipaId,
          memberId: member.healthPlanMemberId,
          membKeyId: member.memberKeyId,
          name:
            member.lastName || member.firstName
              ? `${member.lastName}, ${member.firstName}`
              : "",
          dob: formatNavDob(member.birthDate),
        }),
      });
    },
    [navigate, ipaId],
  );

  // "View more" -> the full-page Hospital/ED route (src/hospitalEd/
  // HospitalEdPage.tsx), gated identically to this widget (same flag +
  // "PriorityWorklist" module) via HospitalEdGate, so this link is never
  // shown somewhere it would 404 or dead-end on a gate screen.
  const handleShowMore = useCallback(() => {
    navigate("/hospital-ed");
  }, [navigate]);

  // Wait for the WHOLE panel before rendering the real card — see
  // UrgentPatientsWidget.tsx's note on `isComplete` vs `!isFetching` for why.
  // Failed panel BEFORE the skeleton — see UrgentPatientsWidget.tsx's note on
  // why a rejected POST otherwise pins this skeleton on Home forever.
  if (isError) return null;
  if (!isComplete) return <HospitalEdEventsCardSkeleton />;
  if (members.length === 0) return null;

  return (
    <HospitalEdEventsCard
      members={members}
      lookbackDays={HOSPITAL_ED_LOOKBACK_DAYS}
      onSelectPatient={handleSelectPatient}
      onShowMore={handleShowMore}
    />
  );
}

/**
 * Panel DOBs arrive as `YYYY-MM-DD`; the nav state (and the PP crosswalk that
 * reads it) wants `MM/DD/YYYY`, which is what the module's own
 * `PatientPriorityIndexSelectedMember.dob` hands WorklistPage. Done by string
 * split rather than dayjs on purpose — parsing a date-only string through a
 * local-timezone `Date` is what shifts a wall-clock midnight back a day.
 */
function formatNavDob(birthDate?: string | null): string {
  if (!birthDate) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  return birthDate;
}
